import React, { useEffect, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { AuthRequired as AuthRequiredType } from 'app/concierge/types';
import useBundleTranslation from 'i18n';
import { IDomain, addAuthorizedTool } from 'store/conciergeSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useHasTokenString } from 'hooks/useHasTokenString';

interface AuthRequiredProps {
    authRequired: AuthRequiredType;
    onRemoveMessage?: () => void;
}

export const AuthRequired: React.FC<AuthRequiredProps> = ({ authRequired, onRemoveMessage }) => {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();

    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains);
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;

    const tool = authRequired.data[0];

    const handleAuth = (authUrl: string) => {
        createTokenInputIfNeeded(tool.tokenFieldName);
        if (isMobile) {
            window.location.href = authUrl;
        } else {
            window.open(authUrl, `Authentication`, 'width=530,height=530');
        }
    };

    const createTokenInputIfNeeded = (tokenFieldName: string) => {
        const existingInput = document.querySelector(`[name="${tokenFieldName}"]`);
        if (!existingInput) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = tokenFieldName;
            input.setAttribute('data-test', tokenFieldName);
            input.value = '';
            document.body.appendChild(input);
        }
    };

    const tokenData = useHasTokenString(tool.tokenFieldName);

    useEffect(() => {
        if (tokenData.hasToken) {
            dispatch(addAuthorizedTool(tool.toolName));
            onRemoveMessage?.();
        }
    }, [tokenData, onRemoveMessage, dispatch, tool.toolName]);

    const relatedDomains = useMemo(() => {
        const result: string[] = [];

        availableDomains.forEach((domain) => {
            const serviceExist = domain.customServices?.find((service) => service.name === tool.toolName);
            if (serviceExist) {
                if (selectedDomains && selectedDomains.length > 0) {
                    if (selectedDomains.includes(domain.name)) {
                        result.push(domain.name);
                    }
                } else {
                    result.push(domain.name);
                }
            }
        });

        return result;
    }, [availableDomains]);

    return (
        <Stack spacing={1}>
            <Typography variant="body2">
                {t('auth_required.to_answer')}{' '}
                {relatedDomains.map((domain, index) => (
                    <React.Fragment key={domain}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            {domain}
                        </Box>
                        {index < relatedDomains.length - 1 && (
                            <>
                                {relatedDomains.length > 2 && index < relatedDomains.length - 1 ? ', ' : ''}
                                {index === relatedDomains.length - 2 && ` ${t('auth_required.or')} `}
                            </>
                        )}
                        {index == relatedDomains.length - 1 && <>{`, `}</>}
                    </React.Fragment>
                ))}{' '}
                {t('auth_required.need_first', { provider: tool.providerName })}{' '}
                {t('auth_required.expires_in', { days: tool.remainDays })}
            </Typography>
            <Button variant="contained" onClick={() => handleAuth(tool.authUrl)} sx={{ width: '80px', height: '28px' }}>
                {t('auth_required.authorize_button')}
            </Button>
        </Stack>
    );
};
